import { ReactElement } from 'react';
import styled from 'styled-components';
import {
  cefRed,
  black,
  white,
  grey10,
  ctaGreen,
  grey30,
} from 'constants/variables.js';
import toRem from 'utils/toRem';
import getTextStringFromReactElement from '../lib/getTextStringFromReactElement.ts';

export interface RadioProps {
  className?: string;
  variant?: string;
  id: string;
  name?: string;
  multiline?: boolean;
  label: ReactElement | string;
  defaultChecked?: boolean;
  checked?: boolean;
  onChange?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  hasErrors?: boolean;
}

interface Multiline {
  multiline?: boolean;
}

const StyledRadio = styled.div`
  display: flex;
`;

const StyledRadioLabel = styled.label<Multiline>`
  width: 100%;
  display: flex;
  ${({ multiline }) =>
    multiline ? 'align-items: flex-start' : 'align-items: center'};
  gap: ${toRem(8)};
  margin: 0;
  padding: ${toRem(10)};
  border: 2px solid ${grey30};
  border-radius: 4px;
  transition: border-color 0.2s ease-in-out;
  cursor: pointer;

  &:before {
    content: '';
    flex-shrink: 0;
    border-radius: 50%;
    display: block;
    width: ${toRem(16)};
    height: ${toRem(16)};
    border: 1px solid ${black};
  }

  &.has-error:before {
    border-color: ${cefRed};
  }
`;

const StyledRadioLabelText = styled.div<Multiline>`
  font-size: ${toRem(14)};
  font-weight: 600;
  word-break: break-word;

  ${({ multiline }) =>
    multiline &&
    `
    display: flex;
    flex-direction: column;
    align-items: flex-start
    `};
`;

const StyledRadioInput = styled.input`
  height: 0;
  width: 0;
  position: absolute;
  opacity: 0;

  &:hover ~ label {
    border-color: ${grey10};

    &:before {
      border: 1px solid ${grey10};
    }
  }

  &:focus ~ label {
    border-color: ${grey10};

    &:before {
      background-color: ${grey10};
      box-shadow: inset 0 0 0 3px ${white};
      border: 1px solid ${grey10};
    }
  }

  &:active ~ label,
  &:checked ~ label {
    border-color: ${ctaGreen};

    &:before {
      background-color: ${ctaGreen};
      box-shadow: inset 0 0 0 3px ${white};
      border: 1px solid ${ctaGreen};
    }
  }

  &:disabled ~ label {
    opacity: 0.3;
    pointer-events: none;
  }
`;

const Radio = ({
  className,
  variant,
  id,
  name,
  multiline,
  label,
  defaultChecked,
  checked,
  onChange,
  onBlur,
  disabled,
  hasErrors,
}: RadioProps) => (
  <StyledRadio
    className={`cef-ui-radio${variant ? ` cef-ui-radio--${variant}` : ''}${
      className ? ` ${className}` : ''
    }`}>
    <StyledRadioInput
      type="radio"
      id={id}
      name={name}
      defaultChecked={defaultChecked}
      checked={checked}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      role="radio"
      aria-label={
        typeof label === 'string' ? label : getTextStringFromReactElement(label)
      }
      aria-checked={defaultChecked || checked}
      aria-invalid={hasErrors || undefined}
    />
    <StyledRadioLabel
      data-testid={`${id}-label`}
      className={hasErrors ? 'has-error' : ''}
      htmlFor={id}
      multiline={multiline}
      aria-hidden="true">
      <StyledRadioLabelText multiline={multiline}>{label}</StyledRadioLabelText>
    </StyledRadioLabel>
  </StyledRadio>
);

export default Radio;
